import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import TopNavigation from './TopNavigation'
import Footer from './Footer'

const TermsOfDonation = () => {
  const [isSecretRoute, setIsSecretRoute] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const hostname = window.location.hostname
    const pathname = window.location.pathname
    const isSecretSubdomain = hostname.startsWith('secret.') || hostname === 'secret.localhost'
    const isSecretPath = pathname.startsWith('/secret')
    setIsSecretRoute(isSecretSubdomain || isSecretPath)
  }, [location.pathname])

  return (
    <div className="min-h-screen bg-background">
      <TopNavigation backTo="/" backText="Back to Home" />

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-foreground mb-8">Terms of Donation</h1>
          <p className="text-muted-foreground mb-8">
            <strong>Last Updated:</strong> September, 2025
          </p>
          <p className="text-muted-foreground mb-8">
            <strong>Tailtations LLC</strong> — A Wyoming Limited Liability Company
          </p>
          <p className="text-muted-foreground leading-relaxed mb-8">
            Welcome to Tailtations® ("Company," "we," "our," or "us"). By making a voluntary contribution ("Donation") through our platform or during any Tailtations event, you ("Donor," "you," or "your") agree to the following Terms of Donation. These terms constitute a legally binding agreement between you and Tailtations LLC.
          </p>

          <div className="p-6 bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 dark:border-yellow-600 rounded-lg mb-8">
            <p className="text-foreground font-semibold text-center leading-relaxed">
              IMPORTANT NOTICE:<br />
              Tailtations LLC is not a nonprofit organization and is not recognized as a 501(c)(3). Donations made to Tailtations LLC are not tax-deductible for federal or state tax purposes.
            </p>
          </div>

          <p className="text-muted-foreground leading-relaxed mb-8">
            Please read these Terms of Donation carefully before submitting any contribution.
          </p>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">1. NON-REFUND POLICY</h2>

              <h3 className="text-xl font-medium text-foreground mb-3">1.1 All Donations Are Final</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                By completing a Donation through our platform, you acknowledge and agree that all contributions are final and non-refundable. Donations cannot be canceled, reversed, or modified once submitted.
              </p>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">1.2 Event Cancellation or Postponement</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If Tailtations cancels or postpones a charitable event due to unforeseen circumstances (e.g., severe weather, venue issues, regulatory restrictions, public health concerns), we will make reasonable efforts to reschedule or redirect donations to a similar charitable purpose, including notifying donors via email of a rescheduled event date within 90 days, providing donors with details of how redirected funds will be used, and offering donors a choice to allocate their funds to a different charitable initiative supported by Tailtations LLC. However, monetary refunds will not be issued for completed donations.
              </p>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">1.3 Use of Donations</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Donations are used to fund the charitable event or cause described at the time of donation. Tailtations LLC retains discretion in how donations are allocated to achieve the stated charitable purpose, including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Purchasing food, supplies, and materials for the charitable event</li>
                <li>Covering operational costs directly related to executing the charitable event</li>
                <li>Redirecting funds to a similar charitable purpose if the original event cannot be completed, such as supporting alternative events serving the same community or cause, purchasing supplies or providing aid to beneficiaries aligned with the original purpose, or funding operational costs for future charitable events with comparable goals</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">2. WYOMING AND CALIFORNIA STATE LAW</h2>

              <h3 className="text-xl font-medium text-foreground mb-3">2.1 Compliance with Wyoming Law</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Under Wyoming contract law and applicable commercial statutes, contributions made through our platform constitute a voluntary financial transaction between you and Tailtations LLC. By donating, you acknowledge that the transaction is final and governed by these terms.
              </p>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">2.2 Compliance with California Law</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                In accordance with California law, including the Consumers Legal Remedies Act (CLRA, Cal. Civ. Code § 1750 et seq.), Tailtations maintains transparency in its Donation process. You acknowledge that:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Donations are voluntary contributions to a for-profit LLC, not a charitable organization.</li>
                <li>The "cooling-off" rule does not apply to charitable donations made through our platform.</li>
                <li>You receive all details about the charitable event and how donations will be used prior to completing the Donation.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">3. DONATION PROCESS</h2>

              <h3 className="text-xl font-medium text-foreground mb-3">3.1 Donation Submission Requirements</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                All Donations must be completed through our online platform at tailtations.com. Donations are considered final only when successfully processed and confirmed via email notification.
              </p>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">3.2 Payment Authorization</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                By providing payment information during the Donation process, you authorize Tailtations LLC and its payment processor (Square) to charge your selected payment method for the full Donation amount, including any applicable fees.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">4. LIMITATION OF LIABILITY</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Tailtations LLC is not liable for:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Delays, cancellations, or modifications to charitable events caused by weather, venue issues, regulatory restrictions, or other external factors beyond our control.</li>
                <li>Donor misunderstandings regarding the tax treatment of Donations or the non-charitable status of Tailtations LLC.</li>
                <li>How donated funds are ultimately allocated, provided they are used in good faith to support the stated charitable purpose.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                To the fullest extent permitted by law, Tailtations LLC's liability for all claims related to your Donation is limited to the amount donated.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">5. TAX DISCLOSURE</h2>

              <h3 className="text-xl font-medium text-foreground mb-3">5.1 Non-Deductible Contribution</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You acknowledge and agree that:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Tailtations LLC is NOT a 501(c)(3) charitable organization.</li>
                <li>Donations made to Tailtations LLC are NOT tax-deductible at the federal or state level.</li>
                <li>Tailtations does not issue charitable donation receipts or documentation for tax deduction purposes.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">6. DISPUTE RESOLUTION</h2>

              <h3 className="text-xl font-medium text-foreground mb-3">6.1 Governing Law</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                These Terms of Donation are governed by the laws of the State of Wyoming. If you reside in California, additional consumer protections may apply.
              </p>

              <h3 className="text-xl font-medium text-foreground mb-3 mt-6">6.2 Arbitration Clause</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Any dispute arising from or relating to these Terms—including interpretation, enforcement, or validity—shall be resolved exclusively through binding arbitration in accordance with the rules of the American Arbitration Association ("AAA").
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Arbitration will occur in Los Angeles, California.</li>
                <li>Each party is responsible for its own legal fees and costs.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">7. CHANGES TO TERMS OF DONATION</h2>
              <p className="text-muted-foreground leading-relaxed">
                Tailtations reserves the right to update, modify, or amend these Terms of Donation at any time. Updated terms will be posted on our platform and will apply to all Donations submitted after the effective update date. For significant changes, we will provide donors with prior notice, such as email notifications to registered donors at least 30 days in advance or a prominent banner on our platform.
              </p>
            </section>

            <section className="mt-8 p-6 bg-muted rounded-lg border-2 border-foreground/20">
              <p className="text-foreground font-semibold text-center leading-relaxed">
                BY MAKING A DONATION, YOU CONFIRM THAT YOU HAVE READ, UNDERSTOOD, AND AGREED TO THESE TERMS OF DONATION.
              </p>
              <p className="text-muted-foreground text-center leading-relaxed mt-4">
                If you do not agree, please do not submit a Donation through our platform.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Contact Information</h2>
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <p className="text-foreground font-medium">Tailtations LLC</p>
                <p className="text-muted-foreground">Email: legal@tailtations.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>
      {!isSecretRoute && <Footer />}
    </div>
  )
}

export default TermsOfDonation
